import { useState } from "react";
import { api } from "../api/client";
import { useAsync } from "../lib/useAsync";
import type { TeamSummary } from "../api/types";

/**
 * Manage the wrestlers linked to the signed-in parent. Lives on the
 * Profile page. Adding a wrestler optionally assigns a team the parent belongs to.
 */
export function MyWrestlers() {
  const wrestlers = useAsync(() => api.listWrestlers(), []);
  const teams = useAsync(() => api.listTeams(), []);
  const [name, setName] = useState("");
  const [teamId, setTeamId] = useState("");
  const [weightClass, setWeightClass] = useState("");
  const [club, setClub] = useState("");
  const [floUrl, setFloUrl] = useState("");
  const [riUrl, setRiUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>();

  const teamName = (id?: string) =>
    (teams.data?.items as TeamSummary[] | undefined)?.find(
      (t) => t.teamId === id,
    )?.name ?? id;

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setErr(undefined);
    try {
      await api.addWrestler({
        displayName: name.trim(),
        teamId: teamId || undefined,
        weightClass: weightClass.trim() || undefined,
        club: club.trim() || undefined,
        floUrl: floUrl.trim() || undefined,
        riUrl: riUrl.trim() || undefined,
      });
      setName("");
      setTeamId("");
      setWeightClass("");
      setClub("");
      setFloUrl("");
      setRiUrl("");
      wrestlers.reload();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (wrestlerId: string, displayName: string) => {
    if (!confirm(`Remove ${displayName} from your profile?`)) return;
    await api.removeWrestler(wrestlerId);
    wrestlers.reload();
  };

  return (
    <div className="card">
      <h2>My wrestlers</h2>
      <p className="muted">
        Add each of your wrestlers. You'll pick which ones you're registering when
        you RSVP to a meet.
      </p>

      {wrestlers.loading && <p>Loading…</p>}
      {wrestlers.error && <p className="error">{wrestlers.error}</p>}
      {wrestlers.data?.items.map((w) => (
        <WrestlerRow
          key={w.wrestlerId}
          w={w}
          teams={teams.data?.items ?? []}
          teamName={teamName}
          onChanged={() => wrestlers.reload()}
          onRemove={() => remove(w.wrestlerId, w.displayName)}
        />
      ))}
      {wrestlers.data && wrestlers.data.items.length === 0 && (
        <p className="muted">No wrestlers added yet.</p>
      )}

      <div className="row" style={{ marginTop: "0.75rem" }}>
        <input
          placeholder="Wrestler's name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          placeholder="Weight (optional)"
          value={weightClass}
          onChange={(e) => setWeightClass(e.target.value)}
        />
        <input
          placeholder="Club (as on FloWrestling, e.g. GPS Wrestling Armonk)"
          value={club}
          onChange={(e) => setClub(e.target.value)}
        />
        <input
          placeholder="Flo athlete URL (optional — most reliable)"
          value={floUrl}
          onChange={(e) => setFloUrl(e.target.value)}
        />
        <input
          placeholder="Wrestling Reimagined profile URL (optional)"
          value={riUrl}
          onChange={(e) => setRiUrl(e.target.value)}
        />
        <select
          style={{ width: "auto" }}
          value={teamId}
          onChange={(e) => setTeamId(e.target.value)}
        >
          <option value="">No team</option>
          {teams.data?.items.map((t) => (
            <option key={t.teamId} value={t.teamId}>
              {t.name ?? t.teamId}
            </option>
          ))}
        </select>
        <button onClick={add} disabled={busy}>
          {busy ? "Adding…" : "Add wrestler"}
        </button>
      </div>
      {err && <p className="error">{err}</p>}
    </div>
  );
}

function WrestlerRow({
  w,
  teams,
  teamName,
  onChanged,
  onRemove,
}: {
  w: import("../api/types").Wrestler;
  teams: TeamSummary[];
  teamName: (id?: string) => string | undefined;
  onChanged: () => void;
  onRemove: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(w.displayName);
  const [weight, setWeight] = useState(w.weightClass ?? "");
  const [club, setClub] = useState(w.club ?? "");
  const [flo, setFlo] = useState(w.floUrl ?? "");
  const [ri, setRi] = useState(w.riUrl ?? "");
  const [teamId, setTeamId] = useState(w.teamId ?? "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.updateWrestler(w.wrestlerId, {
        displayName: name.trim(),
        weightClass: weight.trim(),
        club: club.trim(),
        floUrl: flo.trim(),
        riUrl: ri.trim(),
        teamId: teamId || undefined,
      });
      setEditing(false);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div className="roster-add" style={{ marginTop: "0.5rem" }}>
        <input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <div className="row">
          <input placeholder="Weight" value={weight} onChange={(e) => setWeight(e.target.value)} />
          <input placeholder="Club" value={club} onChange={(e) => setClub(e.target.value)} />
        </div>
        <input placeholder="Flo athlete URL" value={flo} onChange={(e) => setFlo(e.target.value)} />
        <input placeholder="Wrestling Reimagined URL" value={ri} onChange={(e) => setRi(e.target.value)} />
        <select style={{ width: "auto" }} value={teamId} onChange={(e) => setTeamId(e.target.value)}>
          <option value="">No team</option>
          {teams.map((t) => (
            <option key={t.teamId} value={t.teamId}>
              {t.name ?? t.teamId}
            </option>
          ))}
        </select>
        <div className="row">
          <button disabled={busy} onClick={save}>
            {busy ? "Saving…" : "Save"}
          </button>
          <button className="secondary" disabled={busy} onClick={() => setEditing(false)}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="row spread" style={{ marginTop: "0.5rem" }}>
      <span>
        <strong>{w.displayName}</strong>
        {w.weightClass && <span className="muted"> · {w.weightClass}</span>}
        {w.club && <span className="muted"> · {w.club}</span>}
        {w.teamId && <span className="muted"> · {teamName(w.teamId)}</span>}
        {w.floUrl && <span className="muted"> · Flo linked</span>}
      </span>
      <span className="row" style={{ gap: "0.35rem" }}>
        <button className="secondary" onClick={() => setEditing(true)}>
          Edit
        </button>
        <button className="secondary" onClick={onRemove}>
          Remove
        </button>
      </span>
    </div>
  );
}
